"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Mail, Phone, MapPin, Loader, ArrowRight, Calendar as CalendarIcon } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function StorageUnitSelect({
  control
}: {
  control: any;
}) {
  const {
    posts,
    loading
  } = usePosts();
  const availableUnits = posts.filter(post => post.customFields?.status === "Vapaa").map(post => ({
    unitNumber: post.customFields?.unit_number || "",
    size: post.customFields?.size || "",
    price: post.customFields?.price || ""
  }));
  return <FormField control={control} name="storage_unit" rules={{
    required: "Valitse tilatyyppi"
  }} render={({
    field
  }) => <FormItem>
          <FormLabel className="font-serif font-medium">Valitse tila tästä</FormLabel>
          <FormControl>
            <select {...field} disabled={loading} className="flex h-9 w-full items-center justify-between rounded-lg border border-input bg-background px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm font-serif">
              <option value="" disabled>{loading ? "Ladataan tiloja..." : "Valitse tila..."}</option>
              {availableUnits.map((unit, i) => <option key={i} data-index={i} value={unit.unitNumber}>
                  {unit.unitNumber} - {unit.size} m² {unit.price}€/kk
                </option>)}
            </select>
          </FormControl>
          <FormMessage />
        </FormItem>} />;
}
function SyncForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-lg bg-secondary/20 flex items-center justify-center border border-secondary/30">
          <svg className="size-8 text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-default font-semibold text-foreground">
            Viesti lähetetty
          </h3>
          <p className="text-muted-foreground max-w-sm mx-auto">
            {successMessage}
          </p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary text-primary hover:bg-primary/5 rounded-lg">
          Lähetä uusi viesti
        </Button>
      </div>;
  }
  const wp_query_params = {
    post_type: "varasto",
    per_page: 100
  };
  const wp_query = new WP_Query(wp_query_params);
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Nimi on pakollinen"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-serif font-medium">Nimesi</FormLabel>
              <FormControl>
                <Input placeholder="Matti Meikäläinen" {...field} className="bg-background border-input focus-visible:ring-secondary rounded-lg font-serif" />
              </FormControl>
              <FormDescription className="text-xs opacity-70 font-serif">Etu- ja sukunimi</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="birth_date" rules={{
        required: "Syntymäaika on pakollinen"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-serif font-medium">Syntymäaika</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input type="date" {...field} className="bg-background border-input focus-visible:ring-secondary rounded-lg font-serif pl-10" />
                  <SafeHydrate><CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" /></SafeHydrate>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="email" rules={{
        required: "Sähköposti on pakollinen",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Anna voimassa oleva sähköpostiosoite"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-serif font-medium">Sähköpostiosoite</FormLabel>
              <FormControl>
                <Input type="email" placeholder="matti@esimerkki.fi" {...field} className="bg-background border-input focus-visible:ring-secondary rounded-lg font-serif" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-serif font-medium">Yritys</FormLabel>
              <FormControl>
                <Input placeholder="Täytä yrityksesi nimi jos olet yritysasiakas" {...field} className="bg-background border-input focus-visible:ring-secondary rounded-lg font-serif" />
              </FormControl>
              <FormDescription className="text-xs opacity-70 font-serif">Täytä vain, jos varaat yrityksen käyttöön</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="address" rules={{
        required: "Osoite on pakollinen"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-serif font-medium">Osoite</FormLabel>
              <FormControl>
                <Input placeholder="Esimerkkikatu 123" {...field} className="bg-background border-input focus-visible:ring-secondary rounded-lg font-serif" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="city" rules={{
        required: "Kaupunki on pakollinen"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-serif font-medium">Kaupunki</FormLabel>
              <FormControl>
                <Input placeholder="Mikkeli" {...field} className="bg-background border-input focus-visible:ring-secondary rounded-lg font-serif" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="zip_code" rules={{
        required: "Postinumero on pakollinen"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-serif font-medium">Postinumero</FormLabel>
              <FormControl>
                <Input placeholder="50100" {...field} className="bg-background border-input focus-visible:ring-secondary rounded-lg font-serif" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-serif font-medium">Puhelinnumero</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="040 123 4567" {...field} className="bg-background border-input focus-visible:ring-secondary rounded-lg font-serif" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="reservation_start_date" rules={{
        required: "Valitse aloituspäivämäärä"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-serif font-medium">Varauksen aloitus päivämäärä</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input type="date" {...field} className="bg-background border-input focus-visible:ring-secondary rounded-lg font-serif pl-10" />
                  <SafeHydrate><CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" /></SafeHydrate>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 gap-5">
        <PostsProvider wp_query={{
        post_type: "varasto",
        per_page: 100
      }}>
          <StorageUnitSelect control={control} />
        </PostsProvider>
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-sm text-destructive font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-default font-semibold group bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-md rounded-lg transition-all duration-300">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Lähetetään...
          </> : <>
            Lähetä
            <ArrowRight className="ml-2 size-4 group-hover:translate-x-1 transition-transform" />
          </>}
      </Button>
    </Form>;
}
export default function SyncWithUs() {
  const {
    ref: leftRef,
    isVisible: isLeftVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: isRightVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="223960" id="kysy-lisää-tiloistamme" className="bg-background text-foreground relative py-24 lg:py-32 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-[0.05]"></div>
        <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-secondary/5 rounded-lg blur-[120px] opacity-50"></div>
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-primary/5 rounded-lg blur-[100px] opacity-30"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-7xl">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          
          <div ref={leftRef} className={`lg:col-span-5 space-y-10 transition-all duration-1000 ease-out ${isLeftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="space-y-6">
              
              
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold tracking-tighter text-primary leading-[1.1]">
                Varaa tilaa <br className="hidden lg:block" />
                <span className="text-secondary">
                  tavaroillesi.
                </span>
              </h2>
              
              <p className="text-lg font-serif text-muted-foreground max-w-md leading-relaxed"><br /></p>
            </div>

            <div className="space-y-6 pt-4 border-t border-border">
              <div className="flex items-start gap-4 group">
                <div className="mt-1 p-3 rounded-xl bg-card border border-border text-primary group-hover:scale-110 group-hover:bg-secondary/10 group-hover:text-secondary transition-all duration-300 shadow-sm">
                  <Mail className="size-5" />
                </div>
                <div>
                  <p className="text-sm font-default font-semibold text-muted-foreground mb-1">Sähköposti</p>
                  <p className="text-base font-mono font-medium text-foreground">kiinteisto.myyrylainen@surffi.fi</p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="mt-1 p-3 rounded-xl bg-card border border-border text-primary group-hover:scale-110 group-hover:bg-secondary/10 group-hover:text-secondary transition-all duration-300 shadow-sm">
                  <Phone className="size-5" />
                </div>
                <div>
                  <p className="text-sm font-default font-semibold text-muted-foreground mb-1">Puhelinnumero</p>
                  <p className="text-base font-mono font-medium text-foreground">0440 222 136</p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="mt-1 p-3 rounded-xl bg-card border border-border text-primary group-hover:scale-110 group-hover:bg-secondary/10 group-hover:text-secondary transition-all duration-300 shadow-sm">
                  <MapPin className="size-5" />
                </div>
                <div>
                  <p className="text-sm font-default font-semibold text-muted-foreground mb-1">Osoite</p>
                  <p className="text-base font-serif font-medium text-foreground">Kovalantie112, 50200 Mikkeli</p>
                </div>
              </div>
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 delay-200 ease-out ${isRightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="relative group">
              <div className="absolute -inset-[1px] rounded-3xl bg-gradient-to-b from-secondary/20 via-primary/5 to-transparent opacity-50 group-hover:opacity-100 transition-opacity duration-500 blur-[2px]"></div>
              
              <div className="relative bg-card backdrop-blur-xl border border-border rounded-3xl p-6 sm:p-8 md:p-10 shadow-4xl overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-secondary/5 rounded-lg blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
                
                <div className="mb-8">
                  <h3 className="text-2xl font-default font-bold text-primary mb-2">Varaa haluamasi tila itsellesi tästä lomakkeesta</h3>
                  <p className="text-sm font-serif text-muted-foreground">Täytä alla oleva lomake</p>
                </div>

                <FormProvider formKey="space_inquiry_form_contact" formId="space_inquiry_form_contact" sectionName="sync-with-us" defaultValues={{
                name: "",
                birth_date: "",
                email: "",
                company: "",
                address: "",
                city: "",
                zip_code: "",
                phone: "",
                reservation_start_date: "",
                storage_unit: ""
              }} validationRules={{
                name: {
                  required: true
                },
                birth_date: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                address: {
                  required: true
                },
                city: {
                  required: true
                },
                zip_code: {
                  required: true
                },
                reservation_start_date: {
                  required: true
                },
                storage_unit: {
                  required: true
                }
              }} submitText="Lähetä kysely" successMessage="Kiitos varauksestasi! Käsittelemme pyyntösi ja palaamme asiaan, voit sulkea sivun.">
                  <SyncForm />
                </FormProvider>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}